import './index.scss';
import './prose.scss';

import AlchemyLogo from './assets/Logo';
import { XIcon } from './assets/share/XIcon';
import { LinkedInIcon } from './assets/share/LinkedInIcon';
import { LinkIcon } from './assets/share/LinkIcon';
import { useAsyncProcessManager } from './tools/async';
import { AsyncProcessManagerContext } from './tools/async/context';
import toast, { Toaster } from 'react-hot-toast';
import { useEffect, useMemo, useState } from 'react';
import {
  ICategory,
  IMainToolsData,
  IMainToolsDataSwipes,
  ISwipeCategory,
} from './api/interfaces';
import { BlobToolsAPI } from './api';
import { LoadingOverlay } from './components/loading';
import { AIService } from './api/ai';
import { useLocation, useNavigate } from 'react-router-dom';
import { SmoothText } from './components/smoothText';
import { StarsGroup } from './assets/StarsGroup';
import SmoothImage from './components/smoothImage/SmoothImage';
import TextareaAutosize from 'react-textarea-autosize';
import { StringTools } from './tools/string';
import { Checkmark } from './assets/Checkmark';
import { QuoteL } from './assets/QuoteL';
import { QuoteR } from './assets/QuoteR';
import { Plus } from './assets/Plus';
import { Accordeon } from './components/accordeon';
import CheckIcon from './components/code/icons/CheckIcon';
import { CloseIcon } from './assets/CloseIcon';
import { NewWayIcon } from './assets/NewWayIcon';
import { SwipeCard } from './components/swipe';

function App() {
  const location = useLocation();
  const navigate = useNavigate();

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  // const slug = useMemo(() => location.pathname.replaceAll('/', ''), [location]);
  const slug = '/' as any;

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const aiService = useMemo(() => new AIService(), []);

  const [data, setData] = useState<
    IMainToolsData | IMainToolsDataSwipes | null
  >(null);
  const [selectedCategory, setSelectedCategory] = useState<
    ICategory | ISwipeCategory | null
  >(null);

  const [textAreaValues, setTextAreaValues] = useState<{
    [key: string]: string;
  }>({});

  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [generatedResponse, setGeneratedResponse] = useState<string | null>(
    null
  );

  const [billingType, setBillingType] = useState<'monthly' | 'yearly'>(
    'monthly'
  );

  const asyncProcessManager = useAsyncProcessManager({
    onFirstActiveStart: () => {
      // setIsLoading(true);
    },
    onAllFinished: () => {
      // setIsLoading(false);
    },
    onError: (e, _, message, process) => {
      if (e.message == 'Network Error') {
        document.dispatchEvent(new CustomEvent('alchemyConnectionLost'));
      } else {
        // toast.error(`🤖 API error '${message}' on ${process.name}`);
        toast.error(`🤖 API error '${message}' on ${process.name}`);
        console.error('🛑 Error detected', e);
      }
    },
  });

  useEffect(() => {
    asyncProcessManager?.doProcess({
      name: 'Get main data',
      onError: () => {
        setIsLoading(false);
        return undefined;
      },
      action: async () => {
        setIsLoading(true);
        const newData =
          slug === 'prompts'
            ? await BlobToolsAPI.getMainData()
            : await BlobToolsAPI.getMainDataSwipes();

        setSelectedCategory(newData?.results?.[0] ?? null);

        setData(newData);
        setIsLoading(false);
      },
    });
  }, []);

  const promptsInCurrentCategory = useMemo(
    () =>
      (selectedCategory ?? data?.results?.[0])?.items ??
      (data?.results?.[0] as any)?.prompts ??
      [],
    [selectedCategory, data]
  );

  // const activePrompt = useMemo(
  //   () =>
  //     data?.results
  //       ?.flatMap(category => category.prompts)
  //       ?.find(item => item.slug === slug),
  //   [data, slug]
  // );
  const activePrompt: any = null;

  const runActivePrompt = () => {
    if (!activePrompt) {
      toast.error('No active prompt');
      return;
    }
    asyncProcessManager.doProcess({
      name: 'Start generation',
      action: async () => {
        setIsGenerating(true);
        setGeneratedResponse('');

        await aiService.generate({
          request: {
            prompt: activePrompt?.id,
            input_fields: textAreaValues,
          },
          onGenerationChunkReceived: chunk => {
            setGeneratedResponse(prev => prev + chunk.diff);
          },
          onGenerationEnded: () => {
            setIsGenerating(false);
          },
        });
      },
    });
  };

  const handleTextAreaChange = (name: string, value: string) => {
    setTextAreaValues(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    console.log(activePrompt);
  }, [activePrompt]);

  useEffect(() => {
    setTextAreaValues({});
  }, [activePrompt]);

  const shareUrl = window.location.href; // Get the current page URL
  const pageTitle = document.title; // Get the current page title (optional)

  const handleShare = (platform: string) => {
    let shareLink = '';

    switch (platform) {
      case 'x':
        shareLink = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareLink = `https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(pageTitle)}`;
        break;
      case 'instagram':
        toast.error(
          'Instagram does not support direct URL sharing. Please share manually.'
        );
        return; // Exit without opening a link
      default:
        return;
    }

    window.open(shareLink, '_blank', 'noopener,noreferrer');
  };

  const [svgContent, setSvgContent] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedCategory?.icon) return;

    setSvgContent(null);

    fetch(
      'https://proxy.bottocloud.com/?url=' +
        encodeURIComponent(selectedCategory.icon)
    )
      .then(res => res.text())
      .then(text => {
        const colored = text.replace(
          /fill=".*?"/g,
          `fill="#${selectedCategory.color}"`
        );
        setSvgContent(colored);
      });
  }, [selectedCategory]);

  const [isMenuDark, setIsMenuDark] = useState<boolean>(false);

  useEffect(() => {
    const graphicsElement = document.querySelector('.graphics');

    const handleScroll = () => {
      const scrollPosition = document.body.scrollTop; // <-- scrollTop у body
      const imagePosition = graphicsElement
        ? graphicsElement.getBoundingClientRect().top + document.body.scrollTop
        : null;

      if (scrollPosition > (imagePosition ?? 0) - 50) {
        setIsMenuDark(true);
      } else {
        setIsMenuDark(false);
      }
    };

    document.body.addEventListener('scroll', handleScroll);

    return () => {
      document.body.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleScrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false); // Закрыть мобильное меню
  };

  return (
    <AsyncProcessManagerContext.Provider value={asyncProcessManager}>
      <Toaster
        containerClassName="ktulhu-toaster"
        containerStyle={{
          zIndex: 999999999999999,
          width: '100%',
        }}
        toastOptions={{
          style: {
            border: '1px solid var(--app-light-1a)',
            background: 'var(--app-toaster-background)',
            color: 'var(--app-text)',
            display: 'flex',
          },
        }}
      />
      <div className="container">
        <header
          className={
            'header' +
            (isMenuDark ? ' menu-dark' : '') +
            (menuOpen ? ' menu-open' : '')
          }
        >
          <div className="group">
            <a href="https://alchemy-app.com">
              <AlchemyLogo dark={isMenuDark} />
            </a>
          </div>
          <nav>
            <ul
              className={
                'nav-links' +
                (menuOpen ? ' menu-open' : '') +
                (isMenuDark ? ' menu-dark' : '')
              }
            >
              <li>
                <a onClick={() => handleScrollToSection('swipes')}>Swipes</a>
              </li>
              <li>
                <a onClick={() => handleScrollToSection('features')}>
                  Features
                </a>
              </li>
              <li>
                <a onClick={() => handleScrollToSection('pricing')}>Pricing</a>
              </li>
              <li>
                <a onClick={() => handleScrollToSection('faqs')}>FAQs</a>
              </li>

              <li>
                <a className="open-app" href="https://web.alchemy-app.com/">
                  <b>Open app</b>
                </a>
              </li>
            </ul>
            <button
              onClick={() => setMenuOpen(prev => !prev)}
              className="mobile-menu"
            >
              <img src="/assets/menu.svg" />
            </button>
          </nav>
        </header>

        <main className={!activePrompt ? ' landing' : ''}>
          {activePrompt ? (
            <>
              <section className="intro">
                <div className="main-group">
                  <>
                    <div className="prompt-information">
                      <h1>{activePrompt?.name}</h1>
                      <p>
                        {activePrompt?.description ??
                          activePrompt?.short_description}
                      </p>
                    </div>
                    <div className="share-section">
                      <p>Share this prompt:</p>
                      <div className="share-buttons">
                        <button onClick={() => handleShare('x')}>
                          <XIcon />
                        </button>
                        <button onClick={() => handleShare('linkedin')}>
                          <LinkedInIcon />
                        </button>
                        <button
                          onClick={async () => {
                            try {
                              await navigator.clipboard.writeText(shareUrl);
                              toast.success('Link copied to clipboard!');
                            } catch (err) {
                              console.error(err);
                              toast.error('Failed to copy');
                            }
                          }}
                        >
                          <LinkIcon />
                        </button>
                      </div>
                    </div>
                  </>
                </div>

                <div className="prompt-card">
                  <div className="heading">TRY IT HERE</div>
                  <div className="fields">
                    {generatedResponse !== null ? (
                      <div
                        className={
                          'generated-text' + (isGenerating ? ' generating' : '')
                        }
                      >
                        <SmoothText
                          markCitations={true}
                          text={generatedResponse}
                          speed={1}
                          enabled={isGenerating}
                        />
                      </div>
                    ) : (
                      <>
                        {activePrompt?.inputs?.map((input: any, index: any) => (
                          <div className="form-group" key={index}>
                            <div className="label">{input.variable_name}</div>
                            <div className="textarea-wrapper">
                              <TextareaAutosize
                                maxRows={7}
                                placeholder={input.placeholder}
                                value={
                                  textAreaValues[input.variable_name] || ''
                                }
                                onChange={e =>
                                  handleTextAreaChange(
                                    input.variable_name,
                                    e.target.value
                                  )
                                }
                              />
                            </div>
                          </div>
                        ))}
                        <button
                          onClick={() => runActivePrompt()}
                          className="run-prompt"
                        >
                          Run this prompt
                        </button>
                      </>
                    )}
                  </div>
                </div>

                {generatedResponse && (
                  <div className="action-section">
                    <button
                      onClick={() =>
                        (window.location.href =
                          'https://web.alchemy-app.com/sign-up')
                      }
                    >
                      Get more of these{' '}
                      {slug === 'prompts' ? 'prompts' : 'swipes'}
                    </button>
                    <div className="loved-by">
                      <StarsGroup />
                      <p>Loved by over 2000 solopreneur</p>
                    </div>
                  </div>
                )}
              </section>
              <section className="qa">
                <h2>
                  Harness the power of AI with <span>Alchemy</span>
                </h2>

                <div className="qa-group">
                  <h4>What’s Alchemy?</h4>
                  <p>
                    Alchemy is designed to empower solopreneurs and small
                    businesses by providing world-class marketing strategies
                    instantly. With over 100 plug-and-play{' '}
                    {slug === 'prompts' ? 'prompts' : 'swipes'}, it simplifies
                    content creation, research, and marketing execution, helping
                    users enhance their marketing ROI with proven, data-backed
                    tactics.
                  </p>
                </div>

                <div className="qa-group">
                  <h4>Who is it for?</h4>
                  <p>
                    Alchemy is perfect for solopreneurs and small business
                    owners who are overwhelmed by the complexity of marketing
                    tools and strategies. It’s for individuals who want to
                    leverage AI without spending thousands of hours in research
                    or becoming experts themselves. With Alchemy, users can skip
                    the guesswork and instantly access battle-tested marketing
                    strategies that drive real results.
                  </p>
                </div>

                <div className="qa-group">
                  <h4>Why Alchemy?</h4>
                  <p>
                    Most solopreneurs struggle with mediocre marketing results
                    due to generic prompts and overwhelming AI tools. Alchemy
                    simplifies this process by providing expertly curated
                    prompts and marketing tools that eliminate guesswork.
                    Whether you’re struggling with content creation, paid ads,
                    or audience targeting, Alchemy cuts through the noise and
                    delivers high-impact marketing strategies, helping you save
                    time and increase conversion rates effortlessly.
                  </p>
                </div>

                <div className="qa-group">
                  <h4>Are you looking for prompts made by an expert?</h4>
                  <p>
                    Check out our prompt library, or send us an email at{' '}
                    <a href="mailto:hello@alchemy-app.com">
                      hello@alchemy-app.com
                    </a>{' '}
                    with your idea for a prompt, and we’ll designed prompts with
                    knowledge from industry experts.
                  </p>
                </div>
              </section>
              <section className="prompts-and-categories-wrapper">
                <h2>
                  Check out our other{' '}
                  {slug === 'prompts' ? 'prompts' : 'swipes'}
                </h2>

                <div className="prompts-and-categories">
                  <div className="categories">
                    {data?.results?.map((category, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedCategory(category)}
                        className={
                          'category' +
                          (category.id === selectedCategory?.id
                            ? ' active'
                            : '')
                        }
                      >
                        <img src={category.icon} alt="Category icon" />
                        <span>{category.name ?? category.title}</span>
                      </div>
                    ))}
                  </div>
                  <div className="prompts">
                    {promptsInCurrentCategory?.map((prompt: any) => (
                      <>
                        <div onClick={() => {}} className="prompt">
                          <p className="title">{prompt.name}</p>
                          <p className="description">
                            {prompt.description ?? prompt.short_description}
                          </p>
                        </div>
                      </>
                    ))}
                  </div>
                </div>
              </section>
              <section className="promo">
                <h2>Time to make the best of AI for marketing</h2>
                <p>You’re in for a surprise.</p>

                <button
                  onClick={() =>
                    (window.location.href =
                      'https://web.alchemy-app.com/sign-up')
                  }
                >
                  Start 7-day free trial
                </button>
              </section>
            </>
          ) : (
            <>
              <section className="intro-landing landing-section">
                <div className="gradient-bg" />
                <div className="gray-bg-on-first-slide"></div>
                <div className="main-info">
                  <h1>
                    Transform your marketing with AI, built for solopreneurs
                  </h1>

                  <div className="advantages">
                    <div className="advantage">
                      <span className="icon">
                        <CheckIcon />
                      </span>
                      <span className="text">
                        Skip thousands of hours in research and strategy
                      </span>
                    </div>
                    <div className="advantage">
                      <span className="icon">
                        <CheckIcon />
                      </span>
                      <span className="text">
                        Apply world class strategies instantly with over 100
                        plug & play {slug === 'prompts' ? 'prompts' : 'swipes'}
                      </span>
                    </div>
                    <div className="advantage">
                      <span className="icon">
                        <CheckIcon />
                      </span>
                      <span className="text">
                        Boost your marketing ROI with data-backed, proven
                        tactics
                      </span>
                    </div>
                  </div>

                  <div className="start-link">
                    <button
                      onClick={() =>
                        (window.location.href =
                          'https://web.alchemy-app.com/sign-up')
                      }
                    >
                      Started for free
                    </button>

                    <div className="loved-by">
                      <StarsGroup />
                      <p>Loved by 2000+ marketers</p>
                    </div>
                  </div>
                </div>

                <div className="badges">
                  <img src="/assets/badges/badge-1.svg" />
                  <img src="/assets/badges/badge-2.svg" />
                  <img src="/assets/badges/badge-3.svg" />
                  <img src="/assets/badges/badge-4.svg" />
                </div>

                <div className="graphics">
                  <SmoothImage alt="Chat screenshot" src="/assets/chat.webp" />
                </div>

                <img src="/assets/llms.svg" className="llms" />
              </section>
              <section
                id="swipes"
                className="prompts-and-categories-wrapper landing-section"
              >
                <div className="badge">
                  {slug === 'prompts' ? 'PROMPTS' : 'SWIPES'}
                </div>

                <h2>
                  Go from generic to genius with the best prompts on the market
                </h2>

                <p>Plug & play prompts: Your secret weapon</p>

                <div className="prompts-and-categories">
                  <div className="categories">
                    {data?.results?.map((category, index) => (
                      <div
                        key={index}
                        onClick={() => setSelectedCategory(category)}
                        className={
                          'category' +
                          (category.id === selectedCategory?.id ||
                          (index === 0 && !selectedCategory)
                            ? ' active'
                            : '')
                        }
                      >
                        <img src={category.icon} />
                        <span>{category.name ?? category.title}</span>
                      </div>
                    ))}
                  </div>
                  <div className="prompts">
                    {promptsInCurrentCategory?.map((prompt: any) => (
                      <>
                        {prompt?.slug ? (
                          <>
                            <div
                              onClick={() => {
                                if (prompt?.slug) {
                                  document.location.href =
                                    'https://web.alchemy-app.com/demo/' +
                                    prompt.slug;
                                  document.body.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                  });
                                } else {
                                  document.location.href =
                                    'https://web.alchemy-app.com/swipes/remix/' +
                                    BlobToolsAPI.encryptor.encrypt(prompt.id);
                                  document.body.scrollTo({
                                    top: 0,
                                    behavior: 'smooth',
                                  });
                                }
                              }}
                              className="prompt"
                            >
                              <div className="prompt-main">
                                <div
                                  className="category-icon"
                                  style={
                                    {
                                      '--color': '#' + selectedCategory?.color,
                                    } as any
                                  }
                                >
                                  <div
                                    dangerouslySetInnerHTML={
                                      { __html: svgContent } as any
                                    }
                                  />
                                </div>
                                <p className="title">
                                  {StringTools.truncate(
                                    prompt?.name ?? prompt?.title ?? '',
                                    35
                                  )}
                                </p>
                                <p className="description">
                                  {StringTools.truncate(
                                    prompt?.description ??
                                      prompt?.short_description ??
                                      '',
                                    130
                                  )}
                                </p>
                              </div>
                              <div className="prompt-nav">
                                <span></span>
                                <img src="/assets/chevron-right.svg" alt="" />
                              </div>
                            </div>
                          </>
                        ) : (
                          <>
                            <SwipeCard
                              key={prompt?.id}
                              swipe={prompt}
                              onClick={function (): void {
                                document.location.href =
                                  'https://web.alchemy-app.com/swipes/remix/' +
                                  BlobToolsAPI.encryptor.encrypt(prompt.id);
                              }}
                            />
                          </>
                        )}
                      </>
                    ))}
                  </div>
                </div>
              </section>
              <section className="comparison-new prompts-and-categories-wrapper landing-section">
                <div className="badge">COMPARISON</div>

                <h2>Marketing just got effortless</h2>

                <p>Done-for-you AI marketing toolkit that actually works</p>

                <div className="old-new-way">
                  <div className="old">
                    <h3>Old Way</h3>
                    <div className="list">
                      <div className="item">
                        <CloseIcon />
                        <p>
                          Messing around with random{' '}
                          {slug === 'prompts' ? 'prompts' : 'swipes'}, hoping
                          they work
                        </p>
                      </div>
                      <div className="item">
                        <CloseIcon />
                        <p>
                          Spending hours trying to “learn prompt engineering”
                        </p>
                      </div>
                      <div className="item">
                        <CloseIcon />
                        <p>Trying tool after tool that promised results</p>
                      </div>
                      <div className="item">
                        <CloseIcon />
                        <p>Feeling like you’re falling behind in the AI race</p>
                      </div>
                      <div className="item">
                        <CloseIcon />
                        <p>Googling “best marketing strategies” at 11pm</p>
                      </div>
                    </div>
                  </div>
                  <div className="new">
                    <h3>New Way</h3>
                    <div className="list">
                      <div className="item">
                        <NewWayIcon />
                        <p>
                          Getting expert strategies baked into every output – no
                          guesswork
                        </p>
                      </div>
                      <div className="item">
                        <NewWayIcon />
                        <p>
                          Using AI like a pro, without needing to know how any
                          of it works
                        </p>
                      </div>
                      <div className="item">
                        <NewWayIcon />
                        <p>Finally getting content that actually delivers</p>
                      </div>
                      <div className="item">
                        <NewWayIcon />
                        <p>Executing like the top 0.1% on autopilot</p>
                      </div>
                      <div className="item">
                        <NewWayIcon />
                        <p>
                          Instantly tapping into proven strategies – no rabbit
                          holes required
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section className="benefits-new prompts-and-categories-wrapper landing-section">
                <div className="badge">BENEFITS</div>

                <h2>
                  Access world-class {slug === 'prompts' ? 'prompts' : 'swipes'}
                  .
                </h2>

                <p>Apply them instantly. Grow faster than ever.</p>

                <div className="benefits">
                  <div className="benefit-card">
                    <div className="image">
                      <SmoothImage alt="image" src="/assets/benefits-1.svg" />
                    </div>
                    <div className="card-main">
                      <h4>Outsmart the competition</h4>
                      <p>
                        Leverage strategies used by the top 0.1%, without
                        spending years studying them.
                      </p>
                    </div>
                  </div>
                  <div className="benefit-card">
                    <div className="image">
                      <SmoothImage alt="image" src="/assets/benefits-2.svg" />
                    </div>
                    <div className="card-main">
                      <h4>Create content that converts</h4>
                      <p>
                        Make content so good, people think you hired a
                        $20k/month agency.
                      </p>
                    </div>
                  </div>
                  <div className="benefit-card">
                    <div className="image">
                      <SmoothImage alt="image" src="/assets/benefits-3.svg" />
                    </div>
                    <div className="card-main">
                      <h4>
                        1-click {slug === 'prompts' ? 'prompts' : 'swipes'},
                        expert level execution
                      </h4>
                      <p>
                        No prompt engineering. No learning curve. Just results
                        that make you look like a pro.
                      </p>
                    </div>
                  </div>
                </div>
              </section>
              <section className="solution-new prompts-and-categories-wrapper landing-section">
                <div className="badge">SOLUTION</div>

                <h2>
                  Achieving top tier results with AI has never been so simple
                </h2>

                <p>Skip months of research, start winning today.</p>

                <div className="solutions">
                  <div className="solution-group">
                    <div className="solution">
                      <div className="number">1</div>
                      <div className="solution-main">
                        <div className="title">Plug in your business.</div>
                        <div className="description">
                          Add your product, service, and brand details. Alchemy
                          tailors every strategy and prompt to your brand.
                        </div>
                      </div>
                    </div>
                    <div className="solution">
                      <div className="number">2</div>
                      <div className="solution-main">
                        <div className="title">Pick what you need.</div>
                        <div className="description">
                          Choose from content, growth strategies, ads, emails,
                          and more — all built to drive results.
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="solution-group">
                    <div className="solution">
                      <div className="number">3</div>
                      <div className="solution-main">
                        <div className="title">Select a proven play.</div>
                        <div className="description">
                          Use battle-tested strategies from the best marketers
                          in the game.
                        </div>
                      </div>
                    </div>
                    <div className="solution">
                      <div className="number">4</div>
                      <div className="solution-main">
                        <div className="title">Generate gold.</div>
                        <div className="description">
                          Expert-level content, ready in seconds , tailored,
                          on-brand, and built to convert.
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section className="testimonials prompts-and-categories-wrapper landing-section">
                <div className="badge">TESTIMONIALS</div>

                <h2>2,000+ users transforming their marketing</h2>

                <p>Take a look at how others are using Alchemy</p>

                <div className="testimonial-cards">
                  <div className="testimonial-card">
                    <div className="image-placeholder"></div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mark Wilson</div>
                        <div className="role">Social Media Manager</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      I’ve tested dozens of AI tools. Alchemy is the only one
                      that feels like it thinks like a marketer.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Nicole M.</div>
                        <div className="role">Product Marketing Manager</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      I used one prompt and made back my subscription in a
                      single email campaign.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">James T.</div>
                        <div className="role">Shopify Store Owner</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="image-placeholder"></div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mark Wilson</div>
                        <div className="role">Social Media Manager</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      Alchemy saves me hours every week. It’s like having a
                      strategist, copywriter, and marketer in one tab.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Cris Y.</div>
                        <div className="role">Fractional CMO</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="image-placeholder"></div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mark Wilson</div>
                        <div className="role">Social Media Manager</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      Clients are blown away by the quality. They think I have a
                      secret weapon – and I do.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Priya D.</div>
                        <div className="role">Freelance Copywritier</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      I used to freeze up staring at a blank doc. Now I’m
                      banging out launch emails in 20 minutes.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Zoe L.</div>
                        <div className="role">Creator & Course Builder</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      It’s scary good. I plugged in my info and out came a
                      killer lead magnet sequence.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Amina K.</div>
                        <div className="role">Business Coach</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="image-placeholder"></div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mark Wilson</div>
                        <div className="role">Social Media Manager</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      The prompts are 🔥. I’ve doubled my open rates since
                      switching to Alchemy.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mike S.</div>
                        <div className="role">Growth Consultant</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="quote">
                      Every output feels on-brand. It actually understands my
                      voice – wild.
                    </div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Luis V.</div>
                        <div className="role">Solo Founder</div>
                      </div>
                    </div>
                  </div>
                  <div className="testimonial-card">
                    <div className="image-placeholder"></div>
                    <div className="meta">
                      <SmoothImage src="/assets/avatar.png" alt="avatar" />
                      <div className="info">
                        <div className="name">Mark Wilson</div>
                        <div className="role">Social Media Manager</div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>
              <section
                id="features"
                className="workspace-new prompts-and-categories-wrapper landing-section"
              >
                <div className="badge">WORKSPACE</div>

                <h2>An AI workspace for people who mean business</h2>

                <p>
                  A focused AI workspace where proven strategies turn into real
                  results — fast.
                </p>

                <div className="workspace-cards">
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-1.svg" alt="image" />
                    <h4>Use GPT-4, Claude, and more</h4>
                    <p>
                      Choose the right model for your style or task — it’s all
                      built-in.
                    </p>
                  </div>
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-2.svg" alt="image" />
                    <h4>New expert swipes every week</h4>
                    <p>
                      We track what’s working across top brands so you don’t
                      have to.
                    </p>
                  </div>
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-3.svg" alt="image" />
                    <h4>Instantly apply proven strategies</h4>
                    <p>
                      No more prompt engineering. Just select a swipe and click
                      “Generate.”
                    </p>
                  </div>
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-4.svg" alt="image" />
                    <h4>Tailored to your business</h4>
                    <p>Guided inputs that make everything easier</p>
                  </div>
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-5.svg" alt="image" />
                    <h4>Not sure what to write?</h4>
                    <p>
                      Alchemy pulls from your brand, audience, and tone — so
                      every output feels on-point.
                    </p>
                  </div>
                  <div className="workspace-card">
                    <SmoothImage src="/assets/workspace-6.svg" alt="image" />
                    <h4>One space for strategy + execution</h4>
                    <p>
                      Create, refine, and save - all without leaving your flow.
                    </p>
                  </div>
                </div>
              </section>
              <section id="pricing" className="tarrifs-wrapper">
                <div className="tarrifs">
                  <div className="cards-wrapper">
                    <div className="left-card-wrapper">
                      <div className="heading">Ready to leverage AI?</div>
                      <div className="billing-selector">
                        <div
                          className={`option ${billingType === 'monthly' ? 'active' : ''}`}
                          onClick={() => setBillingType('monthly')}
                        >
                          Monthly
                        </div>
                        <div
                          className={`option ${billingType === 'yearly' ? 'active' : ''}`}
                          onClick={() => setBillingType('yearly')}
                        >
                          Yearly <span>Save 40%</span>
                        </div>
                      </div>
                      <div className="card">
                        <div className="list-wrapper">
                          Free
                          <ul>
                            <li>
                              <Checkmark />
                              1000 Chat Tokens/Day
                            </li>
                            <li>
                              <Checkmark />
                              Prompt Library
                            </li>
                            <li>
                              <Checkmark />
                              Quick Prompts
                            </li>
                            <li>
                              <Checkmark />
                              Speech to Text
                            </li>
                            <li>
                              <Checkmark />
                              Themes
                            </li>
                            <li>
                              <Checkmark />5 Favorite Prompts
                            </li>
                            <li>
                              <Checkmark />5 Favorite Prompts
                            </li>
                            <li>
                              <Checkmark />5 Custom Prompts
                            </li>
                            <li>
                              <Checkmark />5 Documents
                            </li>
                          </ul>
                        </div>
                        <button>Start for free</button>
                      </div>
                    </div>
                    <div className="card paid">
                      <div className="list-wrapper">
                        Pro
                        <div className="price-wrapper">
                          <div className="price">
                            ${billingType === 'monthly' ? '39.99' : '23.99'}
                          </div>
                          per month, billed {billingType}
                        </div>
                        <ul>
                          <li>
                            <Checkmark />
                            Everything in Free
                          </li>
                          <li>
                            <Checkmark />
                            Unlimited Chat Tokens
                          </li>
                          <li>
                            <Checkmark />
                            Unlimited Favorite Prompts
                          </li>
                          <li>
                            <Checkmark />
                            Unlimited Custom Prompts
                          </li>
                          <li>
                            <Checkmark />
                            Unlimited Documents
                          </li>
                          <li>
                            <Checkmark />
                            Unlimited Snippets
                          </li>
                          <li>
                            <Checkmark />
                            Webpage Summarization
                          </li>
                          <li>
                            <Checkmark />
                            YouTube Summarization
                          </li>
                          <li>
                            <Checkmark />
                            AI Search
                          </li>
                          <li>
                            <Checkmark />
                            Image OCR
                          </li>
                          <li>
                            <Checkmark />
                            Chat with PDF
                          </li>

                          <li>
                            <Checkmark />
                            Capture Screenshots
                          </li>
                        </ul>
                      </div>
                      <button>Get Pro</button>
                      <div className="card-badge">Recommended</div>
                    </div>
                  </div>
                </div>
              </section>
              <section id="faqs" className="faq-wrapper">
                <div className="faq">
                  <h4>FAQs</h4>
                  <Accordeon
                    title={
                      <>
                        What is Alchemy? <Plus />
                      </>
                    }
                  >
                    Test text
                  </Accordeon>
                  <Accordeon
                    title={
                      <>
                        Who is Alchemy for? <Plus />
                      </>
                    }
                  />
                  <Accordeon
                    title={
                      <>
                        What differentiates Alchemy from other AI tools?
                        <Plus />
                      </>
                    }
                  />
                  <Accordeon
                    title={
                      <>
                        What are the Knowledge Management features of Alchemy?
                        <Plus />
                      </>
                    }
                  />
                  <Accordeon
                    title={
                      <>
                        Is Alchemy free to use?
                        <Plus />
                      </>
                    }
                  />
                  <Accordeon
                    title={
                      <>
                        How does Alchemy handle user data and privacy?
                        <Plus />
                      </>
                    }
                  />
                </div>
              </section>
              <section className="bottom-hero-wrapper">
                <div className="bottom-hero">
                  <div className="line1">the tool built to get you good</div>
                  <div className="line2">
                    Unleash your
                    <br /> marketing genius
                  </div>
                  <button>Start for free</button>
                  <img src="./assets/bottom-hero.png" alt="bottom hero image" />
                </div>
              </section>
            </>
          )}
        </main>

        <footer>
          <a href="https://alchemy-app.com/">
            <AlchemyLogo />
          </a>
          <nav>
            <ul className="nav-links">
              <li>
                <a href="https://alchemy-app.com/privacy">Privacy</a>
              </li>
              <li>
                <a href="https://alchemy-app.com/terms">Terms</a>
              </li>
              <li>
                <a href="https://alchemy-app.com/support">Support</a>
              </li>
              <li>
                <a href="https://alchemy-app.com/">
                  © {new Date().getFullYear()} Alchemy
                </a>
              </li>
            </ul>
          </nav>
        </footer>
      </div>
      <LoadingOverlay isGlobal={true} active={isLoading} />
    </AsyncProcessManagerContext.Provider>
  );
}

export default App;
